import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import StockCard from './StockCard';
import axios from 'axios';

// Use production API URL if available, otherwise localhost for development
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
    ? 'http://localhost:5001/api' 
    : 'https://your-backend-url.com/api'); // Update this with your deployed backend URL

function Dashboard({ stocks, onStockClick, loading }) {
  const [accuracy, setAccuracy] = useState(null);
  const [accuracyLoading, setAccuracyLoading] = useState(true);
  const [bestStocks, setBestStocks] = useState([]);
  const [bestStocksLoading, setBestStocksLoading] = useState(true);

  // Don't auto-fetch on load - only when user clicks refresh
  // useEffect(() => {
  //   fetchAccuracy();
  //   fetchBestStocks();
  //   const interval = setInterval(() => {
  //     fetchAccuracy();
  //     fetchBestStocks();
  //   }, 60000);
  //   return () => clearInterval(interval);
  // }, []);

  const fetchAccuracy = async () => {
    try {
      const response = await axios.get(`${API_URL}/accuracy`);
      setAccuracy(response.data);
    } catch (err) {
      console.error('Failed to fetch accuracy:', err);
    } finally {
      setAccuracyLoading(false);
    }
  };

  const fetchBestStocks = async () => {
    setBestStocksLoading(true);
    try {
      const response = await axios.get(`${API_URL}/stocks/best`);
      setBestStocks(response.data.best_stocks || []);
    } catch (err) {
      console.error('Failed to fetch best stocks:', err);
      setBestStocks([]); // Clear on error, don't use mock
    } finally {
      setBestStocksLoading(false);
    }
  };

  if (loading && stocks.length === 0) {
    return <div className="loading">Loading dashboard...</div>;
  }

  // Prepare data for charts
  const predictionData = stocks.reduce((acc, stock) => {
    const pred = typeof stock.prediction === 'object' 
      ? stock.prediction.prediction 
      : stock.prediction;
    acc[pred] = (acc[pred] || 0) + 1;
    return acc;
  }, {});

  const sentimentData = stocks.map(stock => {
    const mentions = stock.mentions || {};
    return {
      symbol: stock.symbol,
      positive: mentions.total_positive || 0,
      negative: mentions.total_negative || 0,
      neutral: mentions.total_neutral || 0,
      total: mentions.total_mentions || 0
    };
  }).filter(d => d.total > 0).slice(0, 10);

  const topMentioned = [...stocks]
    .sort((a, b) => {
      const aMentions = a.mentions?.total_mentions || 0;
      const bMentions = b.mentions?.total_mentions || 0;
      return bMentions - aMentions;
    })
    .slice(0, 5);

  const pieData = [
    { name: 'Up', value: predictionData.up || 0, color: '#10b981' },
    { name: 'Down', value: predictionData.down || 0, color: '#ef4444' },
    { name: 'Neutral', value: predictionData.neutral || 0, color: '#6b7280' }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-grid">
        <div className="dashboard-card chart-card">
          <h3>📊 Prediction Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="dashboard-card chart-card">
          <h3>💬 Top Mentioned Stocks</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={topMentioned.map(stock => ({
              symbol: stock.symbol,
              mentions: stock.mentions?.total_mentions || 0
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="symbol" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="mentions" fill="#667eea" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="dashboard-card chart-card">
          <h3>😊 Sentiment Analysis (Top 10)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={sentimentData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="symbol" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="positive" stackId="a" fill="#10b981" />
              <Bar dataKey="negative" stackId="a" fill="#ef4444" />
              <Bar dataKey="neutral" stackId="a" fill="#6b7280" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="dashboard-card stats-card">
          <h3>📈 Market Overview</h3>
          <div className="stats-grid">
            <div className="stat-box">
              <div className="stat-value">{stocks.length}</div>
              <div className="stat-label">Stocks Tracked</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">
                {stocks.reduce((sum, s) => sum + (s.mentions?.total_mentions || 0), 0)}
              </div>
              <div className="stat-label">Total Mentions</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">
                {stocks.filter(s => {
                  const pred = typeof s.prediction === 'object' 
                    ? s.prediction.prediction 
                    : s.prediction;
                  return pred === 'up';
                }).length}
              </div>
              <div className="stat-label">Bullish Predictions</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">
                {stocks.filter(s => {
                  const pred = typeof s.prediction === 'object' 
                    ? s.prediction.prediction 
                    : s.prediction;
                  return pred === 'down';
                }).length}
              </div>
              <div className="stat-label">Bearish Predictions</div>
            </div>
          </div>
        </div>

        <div className="dashboard-card accuracy-card">
          <h3>🎯 Prediction Accuracy</h3>
          {accuracyLoading ? (
            <div className="accuracy-loading">Loading accuracy data...</div>
          ) : accuracy && accuracy.total_checked > 0 ? (
            <div className="accuracy-content">
              <div className="accuracy-main">
                <div className="accuracy-percentage">
                  {accuracy.accuracy_percent}%
                </div>
                <div className="accuracy-stats">
                  <div className="accuracy-stat">
                    <span className="stat-label">Correct:</span>
                    <span className="stat-value correct">{accuracy.correct}</span>
                  </div>
                  <div className="accuracy-stat">
                    <span className="stat-label">Incorrect:</span>
                    <span className="stat-value incorrect">{accuracy.incorrect}</span>
                  </div>
                  <div className="accuracy-stat">
                    <span className="stat-label">Total Checked:</span>
                    <span className="stat-value">{accuracy.total_checked}</span>
                  </div>
                </div>
              </div>
              {Object.keys(accuracy.by_prediction).length > 0 && (
                <div className="accuracy-by-type">
                  <h4>Accuracy by Prediction Type:</h4>
                  <div className="accuracy-types">
                    {Object.entries(accuracy.by_prediction).map(([type, data]) => (
                      <div key={type} className="accuracy-type-item">
                        <span className="type-name">{type.toUpperCase()}</span>
                        <span className="type-accuracy">{data.accuracy}%</span>
                        <span className="type-count">({data.correct}/{data.total})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="accuracy-empty">
              No predictions checked yet. Check back in 24 hours for accuracy data.
            </div>
          )}
        </div>
      </div>

      <div className="best-stocks-section">
        <div className="section-header">
          <h2>🏆 Top 5 Best Stocks to Invest In</h2>
          <button onClick={fetchBestStocks} className="btn btn-secondary btn-sm">
            🔄 Refresh
          </button>
        </div>
        {bestStocksLoading ? (
          <div className="loading">Analyzing best investment opportunities...</div>
        ) : bestStocks.length === 0 && !bestStocksLoading ? (
          <div className="no-results">
            <p>Unable to fetch stock data at the moment. This may be due to:</p>
            <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '10px' }}>
              <li>Yahoo Finance rate limiting</li>
              <li>Network connectivity issues</li>
              <li>Temporary API unavailability</li>
            </ul>
            <p style={{ marginTop: '15px' }}>Please try again in a few moments.</p>
          </div>
        ) : bestStocks.length > 0 ? (
          <div className="best-stocks-grid">
            {bestStocks.map((stock, index) => {
              const prediction = typeof stock.prediction === 'object' 
                ? stock.prediction.prediction 
                : stock.prediction;
              const confidence = typeof stock.prediction === 'object' 
                ? stock.prediction.confidence 
                : null;
              
              return (
                <div key={stock.symbol} className="best-stock-card">
                  <div className="best-stock-rank">#{index + 1}</div>
                  <div className="best-stock-header">
                    <div>
                      <h3>{stock.symbol}</h3>
                      <p className="best-stock-name">{stock.name}</p>
                    </div>
                    <div className="best-stock-score">
                      <span className="score-label">Score</span>
                      <span className="score-value">{stock.investment_score}</span>
                    </div>
                  </div>
                  
                  <div className="best-stock-price">
                    <span className="price">${stock.current_price?.toFixed(2)}</span>
                    <span className={`price-change ${stock.price_change >= 0 ? 'positive' : 'negative'}`}>
                      {stock.price_change >= 0 ? '+' : ''}
                      {stock.price_change?.toFixed(2)} ({stock.price_change_percent >= 0 ? '+' : ''}
                      {stock.price_change_percent?.toFixed(2)}%)
                    </span>
                  </div>

                  <div className="best-stock-details">
                    <div className="detail-item">
                      <span className="detail-label">Prediction:</span>
                      <span className={`detail-value prediction-${prediction}`}>
                        {prediction.toUpperCase()} {confidence ? `(${confidence}%)` : ''}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Mentions:</span>
                      <span className="detail-value">{stock.mentions?.total_mentions || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Sentiment:</span>
                      <span className="detail-value">
                        {stock.mentions?.total_positive || 0} positive, {stock.mentions?.total_negative || 0} negative
                      </span>
                    </div>
                  </div>

                  <button 
                    className="btn btn-primary btn-full"
                    onClick={() => onStockClick(stock.symbol)}
                  >
                    View Details
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-results">No best stocks found. Try refreshing.</div>
        )}
      </div>

      <div className="stocks-section">
        <h2>All Stock Cards</h2>
        <div className="stocks-grid">
          {stocks.map((stock) => (
            <StockCard
              key={stock.symbol}
              stock={stock}
              onClick={() => onStockClick(stock.symbol)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

